
import React, { useState, useEffect } from 'react';
import { ExternalLink, Twitter } from 'lucide-react';
import { SiteAvailability } from '../types';

interface MaintenancePageProps {
  config: SiteAvailability;
}

const THEMES: Record<string, any> = {
  maintenance: {
    emoji: '🔧',
    bg: 'linear-gradient(140deg, #09111F 0%, #0C1C3A 55%, #071529 100%)',
    orb1: '#1864D4',
    orb2: '#0E6E9E',
    ringBg: 'rgba(24, 100, 212, 0.22)',
    ringGlow: '0 0 28px rgba(24, 100, 212, 0.55)',
    titleC: '#C8DEFF',
    msgC: '#6A9CC8',
    progTrack: 'rgba(255, 255, 255, 0.08)',
    progFill: '#1864D4',
    labelC: '#6A9CC8',
    etaBg: 'rgba(24, 100, 212, 0.18)',
    etaC: '#5BB8FF',
    acc: '#1864D4',
    accLt: 'rgba(24, 100, 212, 0.25)',
    notifyBg: 'rgba(24, 100, 212, 0.1)',
    notifyBdr: 'rgba(24, 100, 212, 0.35)',
    notifyBtn: '#1864D4',
    notifyBtnC: '#fff',
    socBg: 'rgba(24, 100, 212, 0.1)',
    socBdr: 'rgba(24, 100, 212, 0.3)',
    socC: '#5BB8FF'
  },
  update: {
    emoji: '⬆',
    bg: 'linear-gradient(140deg, #060F0C 0%, #0A1E17 55%, #06130E 100%)',
    orb1: '#0E9E82',
    orb2: '#0B7A5E',
    ringBg: 'rgba(14, 158, 130, 0.22)',
    ringGlow: '0 0 28px rgba(14, 158, 130, 0.55)',
    titleC: '#B8F0E4',
    msgC: '#4EBFA6',
    progTrack: 'rgba(255, 255, 255, 0.08)',
    progFill: '#0E9E82',
    labelC: '#4EBFA6',
    etaBg: 'rgba(14, 158, 130, 0.18)',
    etaC: '#3DDCB8',
    acc: '#0E9E82',
    accLt: 'rgba(14, 158, 130, 0.25)',
    notifyBg: 'rgba(14, 158, 130, 0.1)',
    notifyBdr: 'rgba(14, 158, 130, 0.35)',
    notifyBtn: '#0E9E82',
    notifyBtnC: '#fff',
    socBg: 'rgba(14, 158, 130, 0.1)',
    socBdr: 'rgba(14, 158, 130, 0.3)',
    socC: '#3DDCB8'
  },
  emergency: {
    emoji: '⚠',
    bg: 'linear-gradient(140deg, #140407 0%, #280A0D 55%, #140407 100%)',
    orb1: '#D93545',
    orb2: '#A0222D',
    ringBg: 'rgba(217, 53, 69, 0.22)',
    ringGlow: '0 0 28px rgba(217, 53, 69, 0.6)',
    titleC: '#FFD0D4',
    msgC: '#D07880',
    progTrack: 'rgba(255, 255, 255, 0.08)',
    progFill: '#D93545',
    labelC: '#D07880',
    etaBg: 'rgba(217, 53, 69, 0.18)',
    etaC: '#FF7A87',
    acc: '#D93545',
    accLt: 'rgba(217, 53, 69, 0.25)',
    notifyBg: 'rgba(217, 53, 69, 0.1)',
    notifyBdr: 'rgba(217, 53, 69, 0.35)',
    notifyBtn: '#D93545',
    notifyBtnC: '#fff',
    socBg: 'rgba(217, 53, 69, 0.1)',
    socBdr: 'rgba(217, 53, 69, 0.3)',
    socC: '#FF7A87'
  },
  custom: {
    emoji: '✎',
    bg: 'linear-gradient(140deg, #0A0715 0%, #160E2E 55%, #0A0715 100%)',
    orb1: '#6B3FD9',
    orb2: '#9B2F7A',
    ringBg: 'rgba(107, 63, 217, 0.22)',
    ringGlow: '0 0 28px rgba(107, 63, 217, 0.55)',
    titleC: '#DDD0FF',
    msgC: '#9878D8',
    progTrack: 'rgba(255, 255, 255, 0.08)',
    progFill: '#6B3FD9',
    labelC: '#9878D8',
    etaBg: 'rgba(107, 63, 217, 0.18)',
    etaC: '#C0A0FF',
    acc: '#6B3FD9',
    accLt: 'rgba(107, 63, 217, 0.25)',
    notifyBg: 'rgba(107, 63, 217, 0.1)',
    notifyBdr: 'rgba(107, 63, 217, 0.35)',
    notifyBtn: '#6B3FD9',
    notifyBtnC: '#fff',
    socBg: 'rgba(107, 63, 217, 0.1)',
    socBdr: 'rgba(107, 63, 217, 0.3)',
    socC: '#C0A0FF'
  }
};

export const MaintenancePage: React.FC<MaintenancePageProps> = ({ config }) => {
  const [countdown, setCountdown] = useState({ h: '00', m: '00', s: '00' });
  const [progress, setProgress] = useState(65);

  const pad = (n: number) => String(n).padStart(2, '0');

  useEffect(() => {
    const isPreview = window.location.search.includes('view=maintenance_preview');
    
    const cInt = setInterval(() => {
      if (!config.endTime) return;
      const diff = new Date(config.endTime).getTime() - new Date().getTime();
      if (diff <= 0) {
        setCountdown({ h: '00', m: '00', s: '00' });
        if (config.autoLift && !isPreview) {
          clearInterval(cInt);
          window.location.reload();
        }
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown({ h: pad(h), m: pad(m), s: pad(s) });
    }, 1000);

    let dir = 1;
    const pInt = setInterval(() => {
      setProgress(prev => {
        let next = prev + dir * (Math.random() * 0.5 + 0.1);
        if (next >= 74) dir = -1;
        if (next <= 60) dir = 1;
        return Math.min(99, Math.max(0, next));
      });
    }, 1100);

    return () => {
      clearInterval(cInt);
      clearInterval(pInt);
    };
  }, [config.endTime]);

  const theme = THEMES[config.maintenanceType] || THEMES.maintenance;

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 font-['Sora'] overflow-hidden" style={{ background: theme.bg }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes drift1 { 0%, 100% { transform: translate(0,0) } 50% { transform: translate(20px,-16px) } }
        @keyframes drift2 { 0%, 100% { transform: translate(0,0) } 50% { transform: translate(-14px,16px) } }
        .pv-orb1 { animation: drift1 9s ease-in-out infinite; }
        .pv-orb2 { animation: drift2 11s ease-in-out infinite; }
      `}} />

      <div className="absolute w-[400px] h-[400px] top-[-100px] left-[-100px] rounded-full opacity-20 blur-[80px] pointer-events-none pv-orb1" style={{ background: theme.orb1 }} />
      <div className="absolute w-[350px] h-[350px] bottom-[-80px] right-[-80px] rounded-full opacity-20 blur-[80px] pointer-events-none pv-orb2" style={{ background: theme.orb2 }} />
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px), linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="relative z-10 flex flex-col items-center text-center max-w-lg w-full">
        <div 
          className="w-24 h-24 rounded-3xl flex items-center justify-center text-5xl mb-8 transition-all duration-500"
          style={{ background: theme.ringBg, boxShadow: theme.ringGlow }}
        >
          {theme.emoji}
        </div>
        
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 transition-colors duration-500" style={{ color: theme.titleC }}>
          {config.pageTitle}
        </h1>
        
        <p className="text-lg leading-relaxed mb-10 transition-colors duration-500" style={{ color: theme.msgC }}>
          {config.visitorMessage}
        </p>

        {config.endTime && (
          <div className="flex gap-4 mb-10">
            {[
              { val: countdown.h, label: 'hours' },
              { val: countdown.m, label: 'minutes' },
              { val: countdown.s, label: 'seconds' },
            ].map((unit, idx) => (
              <React.Fragment key={idx}>
                <div className="flex flex-col items-center gap-2">
                  <div 
                    className="text-3xl md:text-4xl font-extrabold font-mono px-4 py-3 rounded-2xl min-w-[70px] transition-all duration-500"
                    style={{ background: theme.accLt, color: theme.titleC }}
                  >
                    {unit.val}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] transition-colors duration-500" style={{ color: theme.labelC }}>
                    {unit.label}
                  </span>
                </div>
                {idx < 2 && <span className="text-3xl font-bold pt-3 opacity-30 transition-colors duration-500" style={{ color: theme.labelC }}>:</span>}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="w-full max-w-xs mb-10">
          <div className="flex justify-between text-[11px] font-bold mb-2 transition-colors duration-500" style={{ color: theme.labelC }}>
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden transition-all duration-500" style={{ background: theme.progTrack }}>
            <div 
              className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%`, background: theme.progFill }}
            />
          </div>
        </div>

        <div className="flex gap-4">
          {config.statusPageUrl && (
            <a 
              href={config.statusPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-bold px-5 py-2.5 rounded-full border transition-all duration-500 flex items-center gap-2"
              style={{ background: theme.socBg, borderColor: theme.socBdr, color: theme.socC }}
            >
              <ExternalLink size={14} /> Status page
            </a>
          )}
          <a 
            href="#" 
            className="text-[11px] font-bold px-5 py-2.5 rounded-full border transition-all duration-500 flex items-center gap-2"
            style={{ background: theme.socBg, borderColor: theme.socBdr, color: theme.socC }}
          >
            <Twitter size={14} /> @status
          </a>
        </div>
      </div>

      <div className="absolute bottom-8 text-[10px] font-bold tracking-[0.3em] uppercase opacity-20" style={{ color: theme.labelC }}>
        Smart School Flow · System Status
      </div>
    </div>
  );
};
